import { useAuth } from "@/_core/hooks/useAuth";
import { useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { getOrCreateDeviceId } from "@/lib/deviceId";
import { useEffect } from "react";
import { isPremiumApp, PREMIUM_TRIAL_LIMIT, PROFESSIONAL_TRIAL_LIMIT } from "@shared/tiers";
import { acceptServerUsage, usageScope, useUsageSnapshot } from "@/lib/usageGate";

/**
 * Real usage/Pro status, sourced from the server (not localStorage).
 *
 * Policy:
 * - Anonymous (logged-out) visitors get a real daily count too, keyed by
 *   an anonymous device ID (see lib/deviceId.ts) -- the free tier no
 *   longer requires an account, only continuing past it does.
 * - Logged-in users get the server's real daily count + Pro status.
 * - Fails OPEN: if the check errors or hasn't loaded yet, we never block a
 *   legitimate user over a transient issue.
 *
 * `isLoading` is exposed specifically so a consumer whose own "fail open"
 * means something stricter than the general default here (e.g. a size
 * limit, where the safe default is the Pro-level limit, not the free
 * one) can make that call itself, rather than silently getting `isPro:
 * false` during the moment the real status hasn't arrived yet -- which is
 * exactly what let an admin get blocked at the free-tier size limit right
 * after loading a page, before their real Pro/admin status had loaded.
 *
 * toolId is auto-derived from the URL (same pattern as useLogToolUsage/
 * FileDropZone/DownloadButton) -- this used to be hardcoded to the
 * literal string "general" regardless of which tool was actually being
 * viewed, which meant the server's usage.check had no way to apply a
 * different limit for a specific tool (like Premium Applications' lower daily
 * cap) since every call looked identical no matter the real page.
 */
export function useFreemium() {
  const { isAuthenticated } = useAuth();
  const params = useParams<{ id?: string }>();
  const toolId = params.id ?? "general";
  const deviceId = isAuthenticated ? undefined : getOrCreateDeviceId();
  const scope = usageScope(isAuthenticated, deviceId, isPremiumApp(toolId));

  const usage = trpc.usage.check.useQuery({ toolId, deviceId }, { refetchInterval: 60_000 });
  // The shared count the tools themselves update the instant a use is
  // counted. The banner used to read only this query, which refreshed after
  // a round trip (or not at all) -- QA saw "9 remaining" stay at 9 while the
  // server count went 1 -> 2 in three tools (00-F). Whatever the query
  // returns is fed into the shared store, and the banner reads the store.
  const shared = useUsageSnapshot();

  useEffect(() => {
    if (usage.data) acceptServerUsage(scope, usage.data);
  }, [usage.data, scope]);

  const statusUncertain = usage.isLoading || usage.isError;
  const fromStore = shared && shared.scope === scope ? shared : null;
  const data = fromStore ?? usage.data ?? null;

  if (!data) {
    return {
      canUse: true,
      remaining: isPremiumApp(toolId) ? PREMIUM_TRIAL_LIMIT : PROFESSIONAL_TRIAL_LIMIT,
      limit: isPremiumApp(toolId) ? PREMIUM_TRIAL_LIMIT : PROFESSIONAL_TRIAL_LIMIT,
      isPro: false,
      isAdmin: false,
      isAuthenticated,
      isLoading: statusUncertain,
    };
  }

  const { allowed, usageToday, limit, isPro, isAdmin } = data;
  return {
    canUse: allowed,
    remaining: Math.max(0, limit - usageToday),
    limit,
    isPro,
    isAdmin,
    isAuthenticated,
    isLoading: false,
  };
}
